import {
  BuiltInDimension,
  DimensionSettings,
  SortOrder,
  SortType,
  UniqueAggregation,
} from '@fullstory/proto/fs/api/metrics';
import { Scope } from '@fullstory/proto/fs/api/search';
import {
  DependentEventFilter,
  Event,
  Platform,
  TimeInterval,
  TimeRange,
} from '@fullstory/proto/fs/api/segment';
import { AuditMetadata } from '@fullstory/proto/fs/gcloud/dstore/protoauditstore';
import {
  FunnelDetail,
  FunnelOrientation,
  UnnamedFunnel,
} from '@fullstory/proto/fs/services/dx-objects';
import {
  Color,
  DateRangeId,
  SegmentSettings,
  TimeRangeComparison,
  TrendSteps,
} from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { Duration, int64, timestamp, Timestamp } from '@fullstory/proto/google/protobuf';
import { DEFAULT_TIME_RANGE } from '../savedMetrics';
import { DEFAULT_AUDIT_METADATA, mockAuditMetadata } from '../shared/metadata';

export const DEFAULT_FUNNEL_NAME = 'Test Funnel';
export const FUNNEL_DIMENSION_LIMIT = 10;
export const defaultSegmentSettings: SegmentSettings[] = [
  {
    segmentId: 'everyone',
    segmentScope: Scope.SCOPE_INDIVIDUAL,
  },
];
const defaultEvents = (deps: DependentEventFilter[] = []) => [
  {
    primary: {
      click: {
        any: {},
      },
    },
    deps,
  },
  {
    primary: {
      rageClick: {
        any: {},
      },
    },
    deps,
  },
  {
    primary: {
      change: {
        any: {},
      },
    },
    deps,
  },
];

interface MockFunnelDetailParams {
  id?: string;
  name?: string;
  description?: string;
  metadata?: Partial<AuditMetadata>;
  lastViewed?: timestamp | null;
  starred?: boolean;
  timeRange?: TimeRange;
  timeRangeComparison?: TimeRangeComparison | null;
  inSameSession?: boolean;
  platform?: Platform;
  deps?: DependentEventFilter[];
  segment?: SegmentSettings[];
  dimensionSettings?: DimensionSettings | null;
  events?: Event[];
  timeInterval?: TimeInterval;
  dateRangeId?: DateRangeId;
  trendSteps?: TrendSteps | null;
  aggregation?: UniqueAggregation;
}

export const mockFunnelDetail = ({
  id = 'test',
  description = 'This is a test funnel',
  name = DEFAULT_FUNNEL_NAME,
  metadata = DEFAULT_AUDIT_METADATA,
  lastViewed = Timestamp.now(),
  starred = true,
  timeRange = DEFAULT_TIME_RANGE,
  timeRangeComparison = null,
  inSameSession = false,
  platform = Platform.PLATFORM_WEB,
  deps = [],
  segment = defaultSegmentSettings,
  dimensionSettings = null,
  events = defaultEvents(deps),
  timeInterval = TimeInterval.TIME_INTERVAL_ENTIRE_RANGE,
  dateRangeId = DateRangeId.DATE_RANGE_ID_PAST_MONTH,
  trendSteps = null,
  aggregation = UniqueAggregation.UNIQUE_AGGREGATION_INDIVIDUAL,
}: MockFunnelDetailParams): FunnelDetail => ({
  funnel: {
    funnel: {
      id,
      name,
      description,
      funnelSettings: {
        conversionId: '0' as int64,
        segmentId: '',
        dateRange: {
          dateRangeId,
          startTime: null,
          endTime: null,
        },
        segment,
        dimensionSettings,
        dimensionApplication: null,
        timeRange,
        timeRangeComparison,
        color: Color.COLOR_INVALID,
        timeInterval,
        trendSteps,
      },
      events: {
        constraints: {
          inOrder: true,
          inSameSession,
          platform,
          timeRange,
          completeSessions: false,
          withinTime: Duration.zero,
          eventThresholds: [],
        },
        events,
      },
      aggregation,
    },
    metadata: mockAuditMetadata(metadata),
    isDcmCreated: false,
  },
  starred,
  lastViewed,
});

export const mockUnnamedFunnel = (
  name: string = DEFAULT_FUNNEL_NAME,
  disabledEvents: number[] = [],
): UnnamedFunnel => ({
  funnel: {
    id: '',
    name,
    description: 'This is a test funnel',
    funnelSettings: null,
    events: {
      constraints: {
        inOrder: true,
        inSameSession: false,
        platform: 1,
        timeRange: null,
        completeSessions: false,
        withinTime: Duration.zero,
        eventThresholds: [],
      },
      events: [
        {
          primary: {
            click: {
              any: {},
            },
          },
          deps: [],
        },
        {
          primary: {
            errorClick: {
              any: {},
            },
          },
          deps: [],
        },
        {
          primary: {
            rageClick: {
              any: {},
            },
          },
          deps: [],
        },
      ],
    },
    aggregation: UniqueAggregation.UNIQUE_AGGREGATION_INDIVIDUAL,
  },
  orientation: FunnelOrientation.FUNNEL_ORIENTATION_HORIZONTAL,
  segmentIds: [],
  segments: [
    {
      segmentId: 'everyone',
      segmentScope: Scope.SCOPE_SESSION,
    },
  ],
  dimensionSettings: {
    sortOrder: SortOrder.SORT_ORDER_DESC,
    sortByDefault: false,
    sortType: SortType.SORT_TYPE_INVALID,
    limit: FUNNEL_DIMENSION_LIMIT,
    dimension: {
      builtInProperty: BuiltInDimension.DIMENSION_BROWSER,
      prefix: '',
      limit: 0,
      computeAtLevel: false,
      eventRefinement: null,
    },
    dimensions: [
      {
        builtInProperty: BuiltInDimension.DIMENSION_BROWSER,
        prefix: '',
        limit: 0,
        computeAtLevel: false,
        eventRefinement: null,
      },
    ],
    computeTopLevel: false,
    computeNumGroups: false,
    allowAllPartialResults: false,
    noFallback: false,
  },
  timeRange: null,
  disabledEvents,
  timeRangeComparison: null,
  timeInterval: TimeInterval.TIME_INTERVAL_INVALID,
});
